import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock, MapPin, RefreshCw, CheckCircle, XCircle, User } from 'lucide-react';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import OTPLoginModal from './OTPLoginModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  confirmed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Confirmed' },
  pending: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Pending Payment' },
  cancelled: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Cancelled' },
  completed: { icon: CheckCircle, color: 'text-slate-600', bg: 'bg-slate-50', label: 'Completed' }
};

const MyBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading, getToken } = useCustomerAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setShowLogin(true);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      setShowLogin(false);
      loadBookings();
    }
  }, [isAuthenticated]);

  // Just came back from a successful payment → force a fresh fetch so the new
  // booking shows immediately (and again shortly after, to cover write propagation).
  useEffect(() => {
    if (isAuthenticated && location.state?.refresh) {
      loadBookings();
      const t = setTimeout(() => loadBookings(), 1500);
      return () => clearTimeout(t);
    }
  }, [isAuthenticated, location.state?.refresh]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/public/auth/bookings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBookings(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading bookings:', error);
      setBookings([]);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, mins] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const getFilteredBookings = () => {
    const today = new Date().toISOString().split('T')[0];
    return bookings.filter(booking => {
      if (filter === 'upcoming') return booking.date >= today && booking.status !== 'cancelled';
      if (filter === 'past') return booking.date < today || booking.status === 'cancelled' || booking.status === 'completed';
      return true;
    }).sort((a, b) => filter === 'past'
      ? new Date(b.date) - new Date(a.date)
      : new Date(a.date) - new Date(b.date)
    );
  };

  const filteredBookings = getFilteredBookings();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#0A2C93] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* OTP Login Modal */}
      {showLogin && !isAuthenticated && (
        <OTPLoginModal onClose={() => { setShowLogin(false); navigate('/'); }} />
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
              data-testid="back-to-home"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Home</span>
            </button>
            <h1 className="font-semibold text-slate-900">My Bookings</h1>
            <button
              onClick={loadBookings}
              className="p-2 hover:bg-slate-100 rounded-lg transition"
              data-testid="refresh-bookings"
            >
              <RefreshCw className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>
      </header>

      {/* User Info Bar */}
      {user && (
        <div className="bg-[#0A2C93]/5 border-b border-[#0A2C93]/10">
          <div className="max-w-3xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#0A2C93] flex items-center justify-center">
                  <span className="text-sm font-bold text-white">
                    {(user.name || user.phone || '?')[0].toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="font-medium text-slate-900">{user.name || 'Guest'}</div>
                  <div className="text-sm text-slate-500">+91 {user.phone}</div>
                </div>
              </div>
              <Link to="/profile" className="text-sm text-[#0A2C93] font-medium" data-testid="view-profile">
                Profile →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="sticky top-14 z-40 bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex gap-4">
            {['upcoming', 'past', 'all'].map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`py-3 px-1 text-sm font-medium border-b-2 transition capitalize ${
                  filter === tab
                    ? 'border-[#0A2C93] text-[#0A2C93]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`filter-${tab}`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6">
        {!isAuthenticated ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Login to see your bookings</h3>
            <button
              onClick={() => setShowLogin(true)}
              className="mt-4 px-6 py-3 bg-[#0A2C93] hover:bg-[#082273] text-white font-medium rounded-xl transition"
              data-testid="login-to-view"
            >
              Login
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-[#0A2C93] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <Calendar className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">No bookings found</h3>
            <p className="text-slate-500 mb-6">
              {filter === 'upcoming' 
                ? "You don't have any upcoming bookings" 
                : filter === 'past' 
                  ? "No past bookings yet"
                  : "You haven't made any bookings yet"
              }
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#0A2C93] hover:bg-[#082273] text-white font-medium rounded-xl transition"
              data-testid="browse-venues"
            >
              Browse Venues
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map(booking => {
              const statusConfig = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={booking.id || booking._id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition"
                  data-testid={`booking-card-${booking.id || booking._id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-slate-900">{booking.facility_name || 'Venue'}</h3>
                        {booking.court_id && (
                          <div className="text-sm text-slate-500">{booking.court_id}</div>
                        )}
                      </div>
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {statusConfig.label}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(booking.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Clock className="w-4 h-4" />
                        <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                      </div>
                    </div>

                    {booking.amount != null && (() => {
                      const paid = Number(booking.amount_paid || 0);
                      const due = booking.amount_pending != null ? Number(booking.amount_pending) : Math.max(0, Number(booking.amount) - paid);
                      return (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5" data-testid={`payment-breakdown-${booking.id || booking._id}`}>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-slate-500">Total</span>
                            <span className="font-semibold text-slate-900">₹{Number(booking.amount).toLocaleString('en-IN')}</span>
                          </div>
                          {booking.payment_status === 'paid' ? (
                            <div className="text-xs font-semibold text-green-600" data-testid="pay-fully-paid">Fully paid</div>
                          ) : booking.payment_status === 'partial' ? (
                            <>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">Paid online</span>
                                <span className="font-semibold text-green-600">₹{paid.toLocaleString('en-IN')} paid</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-500">Balance</span>
                                <span className="font-semibold text-orange-600">₹{due.toLocaleString('en-IN')} due at venue</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-slate-500">Balance</span>
                              <span className="font-semibold text-red-600">₹{due.toLocaleString('en-IN')} due</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyBookings;
